import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

// ==================== SVG ICONS ====================

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

// ==================== STYLES ====================

const styles = `
  .cam-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(4px);
  }

  .cam-modal {
    background: #fff;
    border-radius: 8px;
    width: 90%;
    max-width: 360px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    animation: cam-slideIn 0.25s ease;
    border-top: 4px solid #2563eb;
  }

  @keyframes cam-slideIn {
    from {
      opacity: 0;
      transform: translateY(-16px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .cam-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid #e2e8f0;
  }

  .cam-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .cam-header-title svg {
    color: #2563eb;
  }

  .cam-header h3 {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: #1e3a8a;
  }

  .cam-close {
    background: none;
    border: none;
    font-size: 1.25rem;
    color: #94a3b8;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: color 0.2s;
  }

  .cam-close:hover {
    color: #1e3a8a;
  }

  .cam-body {
    padding: 16px;
  }

  .cam-description {
    margin: 0 0 16px 0;
    color: #64748b;
    font-size: 0.75rem;
    line-height: 1.5;
  }

  .cam-form-group {
    margin-bottom: 12px;
  }

  .cam-form-group label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #1e3a8a;
    font-size: 0.75rem;
  }

  .cam-form-group input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 0.85rem;
    background: #fff;
    color: #1e3a8a;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }

  .cam-form-group input:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .cam-form-group input:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }

  .cam-error {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 12px;
    font-size: 0.75rem;
  }

  .cam-error svg {
    flex-shrink: 0;
    color: #ef4444;
  }

  .cam-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 16px;
  }

  .cam-btn {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
  }

  .cam-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .cam-btn-cancel {
    background: #f1f5f9;
    color: #64748b;
    border: 1px solid #e2e8f0;
  }

  .cam-btn-cancel:hover:not(:disabled) {
    background: #e2e8f0;
    color: #1e3a8a;
  }

  .cam-btn-submit {
    background: #2563eb;
    color: white;
  }

  .cam-btn-submit:hover:not(:disabled) {
    background: #1e40af;
  }

  .cam-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: cam-spin 1s linear infinite;
    margin-right: 6px;
    vertical-align: middle;
  }

  @keyframes cam-spin {
    to { transform: rotate(360deg); }
  }
`;

interface ConfigAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ConfigAccessModal: React.FC<ConfigAccessModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const { validatePassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsValidating(true);

    try {
      const result = await validatePassword(password);

      if (result.success) {
        setPassword('');
        onSuccess();
      } else {
        setError(result.message || 'Contraseña incorrecta');
      }
    } catch (err) {
      setError('Error al validar la contraseña');
    } finally {
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="cam-overlay" onClick={handleClose}>
      <style>{styles}</style>
      <div className="cam-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cam-header">
          <div className="cam-header-title">
            <LockIcon />
            <h3>Acceso a Configuración</h3>
          </div>
          <button className="cam-close" onClick={handleClose}>
            ×
          </button>
        </div>
        <div className="cam-body">
          <p className="cam-description">
            Este módulo contiene configuraciones técnicas del sistema. Por favor, ingrese su contraseña para continuar.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="cam-form-group">
              <label htmlFor="config-password">Contraseña</label>
              <input
                id="config-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingrese su contraseña"
                autoFocus
                disabled={isValidating}
              />
            </div>
            {error && (
              <div className="cam-error">
                <AlertIcon />
                {error}
              </div>
            )}
            <div className="cam-actions">
              <button
                type="button"
                className="cam-btn cam-btn-cancel"
                onClick={handleClose}
                disabled={isValidating}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="cam-btn cam-btn-submit"
                disabled={isValidating || !password}
              >
                {isValidating ? (
                  <>
                    <span className="cam-spinner"></span>
                    Validando...
                  </>
                ) : (
                  'Acceder'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
